import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';
import { PersonIdentityVm } from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import { ChangePackageStatus } from '@/features/dashboard/bookingChanges/types/ChangeViewModel.ts';
import { SettlementDistribution } from '@/features/dashboard/bookingChanges/types/SettlementDistribution.ts';
import { ConsentType } from '@/features/dashboard/consentTemplates/types/ConsentTemplateType.ts';

/**
 * Model widoku trzyetapowego wprowadzania paczki: podsumowanie (W1),
 * rozliczenie dopłaty (W2) i widok akceptacji po stronie klienta (W3).
 *
 * Wszystkie trzy kroki opisują ten sam szkic — W1 i W3 są projekcją tych samych
 * pozycji, różnią się wyłącznie tym, ile mechaniki paczek pokazują.
 *
 * Kwoty są listami po jednej pozycji na walutę, tak samo jak w rachunku
 * (`BillViewModel.ts`): paczka potrafi ruszyć złotówki i euro jednocześnie.
 */

/** Pozycja szkicu w podsumowaniu: wartość przed, po i wpływ na kwotę. */
export interface ChangeSummaryItemVm {
  id: string;
  label: string;
  before: string;
  after: string;
  delta: MoneyValueDto[];
}

/** Zmiany zebrane pod osobą, której dotyczą, albo pod całą rezerwacją. */
export interface ChangeSummaryGroupVm {
  id: string;
  /** `null` dla pozycji całej rezerwacji — rezerwacja osobą nie jest. */
  person: PersonIdentityVm | null;
  title: string;
  /** Podpis w wersji operatora („Uczestnik · 6 lat”). */
  roleLabel: string;
  /** Rola w wersji dla klienta („uczestniczka”, „Ty · zapisujący”). */
  clientRoleLabel: string;
  totalDelta: MoneyValueDto[];
  items: ChangeSummaryItemVm[];
}

export interface SettlementOptionVm {
  option: SettlementDistribution;
  title: string;
  description: string;
  /**
   * Czy backend potrafi ten rozkład wykonać. `SchedulePlan` zna jeden, więc
   * dwie pozostałe karty zostają na ekranie wyszarzone — kasowanie ich
   * i dokładanie z powrotem przy dobudowie kosztowałoby więcej niż dopisek.
   */
  available: boolean;
}

/** Wiersz przeliczonego harmonogramu — stara i nowa kwota obok siebie. */
export interface SettlementScheduleRowVm {
  id: string;
  name: string;
  dueDateLabel: string;
  /** Rata ma jedną walutę, ale niesie ją listą jak każda inna kwota modelu. */
  amount: MoneyValueDto[];
  previousAmount?: MoneyValueDto[];
  /** Rata nietknięta przez dopłatę. */
  unchanged?: boolean;
  /** Pozycja, którą paczka dopisuje do harmonogramu (korekta albo zwrot). */
  isNew?: boolean;
  /** Rata zredukowana do zera — zostaje w harmonogramie jako anulowana. */
  cancelled?: boolean;
  /**
   * Czemu rata została nietknięta („opłacona”, „otwarta płatność online”).
   * Pokazujemy to wprost: dwóch z pięciu powodów po statusie raty nie widać.
   */
  frozenLabel?: string;
  frozenHint?: string;
}

/** Harmonogram po zmianie — jeden, bo backend zna jeden rozkład. */
export interface SettlementScheduleVm {
  rows: SettlementScheduleRowVm[];
  totalBefore: MoneyValueDto[];
  totalAfter: MoneyValueDto[];
}

/**
 * Termin i metody płatności linii korekty — jedyne, czego podgląd nie liczy,
 * bo to decyzja operatora. `resolve` wymaga obu przy niezerowej kwocie paczki.
 */
export interface CorrectionTermsVm {
  /** `YYYY-MM-DD`; `null`, gdy nie ma z czego zaproponować terminu. */
  dueDate: string | null;
  paymentMethods: PaymentMethod[];
  /** Metody dopuszczone przez którąkolwiek z rat biorących udział w rozkładzie. */
  methodChoices: PaymentMethod[];
}

/** Trasa wprowadzenia paczki. */
export enum ChangeRoute {
  /** Zapisujący dostaje link i musi zaakceptować. */
  CLIENT_ACCEPTANCE = 'CLIENT_ACCEPTANCE',
  /** Paczka wchodzi natychmiast, bez zgody klienta. */
  IMMEDIATE = 'IMMEDIATE',
}

export interface ChangeRouteOptionVm {
  route: ChangeRoute;
  title: string;
  /** Odznaka przy tytule: „zalecane” / „bez zgody”. */
  tag: string;
  description: string;
}

/**
 * Dokument do ponownej akceptacji przez zapisującego — zgoda wyjazdu widziana
 * przez krok W2.
 *
 * Uzasadnienia („czemu ten dokument jest na liście”) tu nie ma i mieć nie
 * może: nie istnieje reguła, która by je wyprodukowała — lista to po prostu
 * wszystkie zgody wyjazdu.
 */
export interface AcceptanceDocumentVm {
  /** `tourConsentId` — identyfikator zgody wyjazdu, nie osobny byt widoku. */
  id: string;
  name: string;
  /** Rodzaj zgody; razem z `required` stoi na nim podpowiedź systemu. */
  type: ConsentType;
  /** Czy zgoda jest wymagana przy zapisie na wyjazd — druga gałąź podpowiedzi. */
  required: boolean;
  previewUrl?: string;
}

/** Trzy wyjścia z modala kończącego edycję. */
export enum ExitEditingChoice {
  APPLY = 'APPLY',
  SAVE_DRAFT = 'SAVE_DRAFT',
  DISCARD = 'DISCARD',
}

/** Wejście kroku 1 — podsumowanie tego, co zmienia szkic. */
export interface ChangePackageSummaryVm {
  packageNumber: number;
  status: ChangePackageStatus;
  bookingId: string;
  bookingCode: string;
  groups: ChangeSummaryGroupVm[];
  /** Bilans paczki per waluta: dodatnia = dopłata, ujemna = zwrot. */
  balance: MoneyValueDto[];
  itemCount: number;
  financialItemCount: number;
}

/** Wejście kroku 2 — rozkład dopłaty, trasa i dokumenty. */
export interface ChangePackageSettlementVm {
  packageNumber: number;
  balance: MoneyValueDto[];
  paid: MoneyValueDto[];
  /** Ile harmonogram obiecuje przed wprowadzeniem paczki. */
  dueBefore: MoneyValueDto[];
  /** Ile rat bierze udział w rozkładzie — reszta ma podany powód zamrożenia. */
  adjustableCount: number;
  options: SettlementOptionVm[];
  schedule: SettlementScheduleVm;
  correctionTerms: CorrectionTermsVm;
  routes: ChangeRouteOptionVm[];
  documents: AcceptanceDocumentVm[];
}

/**
 * Wejście kroku 3 — ta sama paczka widziana przez osobę zapisującą.
 *
 * Terminu ważności linku tu nie ma i nie będzie: paczka czekająca na klienta
 * nie wygasa sama, a token rezerwacji żyje do końca wyjazdu.
 */
export interface ClientAcceptanceVm {
  bookingCode: string;
  tourName: string;
  dateRangeLabel: string;
  /** Np. „Bobin oraz Twoich danych”. */
  scopeLabel: string;
  groups: ChangeSummaryGroupVm[];
  balance: MoneyValueDto[];
  /** Zdanie tłumaczące, jak dopłata wchodzi w harmonogram. */
  settlementDescription: string;
  documents: AcceptanceDocumentVm[];
  /** ISO-8601 instant; `null`, dopóki paczka nie została wysłana. */
  sentAt: string | null;
}
